import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import {
  CreateBillingDto,
  UpdateBillingDto,
  Billing,
  BillingConcepto,
  BillingFiscalSummary,
  ProductServiceCatalogItem,
  PAYMENT_METHODS,
  PAYMENT_TYPES,
  CFDI_USAGES,
  SATCatalog
} from '../../../data/interfaces/billing.interface';
import { ProductServiceCatalogService } from '../../../data/services/api/product-service-catalog.service';

// Opción del dropdown de Clave: valor = key del catálogo, label enriquecido, type para derivar la Unidad.
interface CatalogOption {
  key: string;
  label: string;
  type: 'PRODUCTO' | 'SERVICIO';
}

const MESES = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

@Component({
  selector: 'app-billing-form',
  templateUrl: './billing-form.component.html',
  styleUrls: ['./billing-form.component.scss']
})
export class BillingFormComponent implements OnInit, OnDestroy {
  @Input() leaseId!: string;
  @Input() initialData?: Billing;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() suggestedRent?: number | null;                 // renta base sin IVA del arrendamiento (sugerencia editable)
  @Input() fiscalSummary?: BillingFiscalSummary;          // datos derivados read-only (cliente/arrendamiento)

  @Output() onSubmit = new EventEmitter<CreateBillingDto | UpdateBillingDto>();
  @Output() onCancel = new EventEmitter<void>();

  billingForm!: FormGroup;
  isSubmitting = false;
  private destroy$ = new Subject<void>();

  // Catálogos SAT (columnas 16/17/18)
  paymentMethods = PAYMENT_METHODS;
  paymentTypes = PAYMENT_TYPES;
  cfdiUsages = CFDI_USAGES;

  // Catálogo Producto/Servicio (dropdown de Clave)
  catalogOptions: CatalogOption[] = [];
  private catalogByKey = new Map<string, CatalogOption>();
  loadingCatalog = false;

  constructor(
    private fb: FormBuilder,
    private catalogService: ProductServiceCatalogService
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.loadCatalog();

    if (this.initialData && this.mode === 'edit') {
      this.loadInitialData();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Formulario reactivo — SOLO los datos que se capturan a mano para el reporte.
   * Razón Social / No. Contrato / No. ID se derivan del cliente/arrendamiento (fiscalSummary, read-only).
   */
  private initializeForm() {
    this.billingForm = this.fb.group({
      paymentMethod: ['', [Validators.required]],   // col 16 Forma de pago
      paymentType: ['', [Validators.required]],     // col 17 Método de Pago
      cfdiUsage: ['', [Validators.required]],        // col 18 Uso de CFDI
      conceptos: this.fb.array([])                   // cols 7-11, 13
    });
  }

  private loadCatalog() {
    this.loadingCatalog = true;
    this.catalogService.getProductServices().subscribe({
      next: (items: ProductServiceCatalogItem[]) => {
        this.catalogOptions = items.map((it) => ({ key: it.key, type: it.type, label: `${it.key} — ${it.name}` }));
        this.catalogByKey.clear();
        this.catalogOptions.forEach((opt) => this.catalogByKey.set(opt.key, opt));
        this.loadingCatalog = false;
      },
      error: () => {
        this.catalogOptions = [];
        this.loadingCatalog = false;
      }
    });
  }

  private loadInitialData() {
    if (!this.initialData) return;

    this.billingForm.patchValue({
      paymentMethod: this.initialData.paymentMethod || '',
      paymentType: this.initialData.paymentType || '',
      cfdiUsage: this.initialData.cfdiUsage || ''
    });

    this.conceptos.clear();
    (this.initialData.conceptos || []).forEach((c) => this.conceptos.push(this.buildConceptoGroup(c)));
  }

  // ---- Conceptos (FormArray) ----

  get conceptos(): FormArray {
    return this.billingForm.get('conceptos') as FormArray;
  }

  private buildConceptoGroup(c?: Partial<BillingConcepto>): FormGroup {
    return this.fb.group({
      position: [c?.position ?? this.conceptos.length + 1],
      quantity: [c?.quantity ?? 1, [Validators.required, Validators.min(0.01)]],
      key: [c?.key ?? '', [Validators.required]],
      unit: [c?.unit ?? '', [Validators.required]],
      unitPrice: [c?.unitPrice ?? null, [Validators.required, Validators.min(0)]],
      comments: [c?.comments ?? '', [Validators.maxLength(250)]]
    });
  }

  addConcepto() {
    this.conceptos.push(this.buildConceptoGroup());
    this.renumberConceptos();
  }

  removeConcepto(index: number) {
    this.conceptos.removeAt(index);
    this.renumberConceptos();
    this.billingForm.markAsDirty();
  }

  private renumberConceptos() {
    this.conceptos.controls.forEach((group, i) => group.get('position')?.setValue(i + 1));
  }

  onConceptoKeyChange(index: number) {
    const group = this.conceptos.at(index);
    const key = group.get('key')?.value;
    const item = key ? this.catalogByKey.get(key) : null;
    group.get('unit')?.setValue(item ? item.type : '');
  }

  applyRentSuggestion(index: number) {
    if (this.suggestedRent == null) return;
    this.conceptos.at(index).get('unitPrice')?.setValue(this.suggestedRent);
    this.conceptos.at(index).markAsDirty();
  }

  get defaultCommentPlaceholder(): string {
    const now = new Date();
    return `Default: RENTA ${MESES[now.getMonth()]} ${now.getFullYear()}`;
  }

  // Resumen en vivo para la tarjeta "Resumen fiscal".
  get conceptosCount(): number {
    return this.conceptos.length;
  }
  get conceptosTotal(): number {
    return this.conceptos.controls.reduce((sum, g) => {
      const qty = Number(g.get('quantity')?.value) || 0;
      const price = Number(g.get('unitPrice')?.value) || 0;
      return sum + qty * price;
    }, 0);
  }

  conceptoHasError(index: number, controlName: string, errorType: string): boolean {
    const control = this.conceptos.at(index)?.get(controlName);
    return (control?.hasError(errorType) && (control?.dirty || control?.touched)) || false;
  }

  // ---- Helpers de campos SAT ----

  getFormControl(controlName: string) {
    return this.billingForm.get(controlName);
  }

  hasError(controlName: string, errorType: string): boolean {
    const control = this.getFormControl(controlName);
    return control?.hasError(errorType) && (control?.dirty || control?.touched) || false;
  }

  getErrorMessage(controlName: string): string {
    const control = this.getFormControl(controlName);
    if (control?.hasError('required')) return this.getRequiredMessage(controlName);
    if (control?.hasError('maxlength')) {
      const maxLength = control.errors?.['maxlength'].requiredLength;
      return `Este campo no puede exceder ${maxLength} caracteres`;
    }
    return '';
  }

  private getRequiredMessage(controlName: string): string {
    const messages: { [key: string]: string } = {
      paymentMethod: 'La forma de pago es requerida',
      paymentType: 'El método de pago es requerido',
      cfdiUsage: 'El uso de CFDI es requerido'
    };
    return messages[controlName] || 'Este campo es requerido';
  }

  getDropdownLabel(options: SATCatalog[], value: string): string {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  onFormSubmit() {
    if (this.billingForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.renumberConceptos();

      const formValue = this.billingForm.value;
      const conceptos: BillingConcepto[] = (formValue.conceptos || []).map((c: any) => ({
        position: c.position,
        quantity: c.quantity,
        key: c.key,
        unit: c.unit,
        unitPrice: c.unitPrice,
        comments: (c.comments || '').trim()
      }));

      const payload = {
        paymentMethod: formValue.paymentMethod,
        paymentType: formValue.paymentType,
        cfdiUsage: formValue.cfdiUsage,
        conceptos
      };

      if (this.mode === 'create') {
        this.onSubmit.emit({ ...payload, leaseId: this.leaseId } as CreateBillingDto);
      } else {
        this.onSubmit.emit(payload as UpdateBillingDto);
      }

      this.isSubmitting = false;
    } else {
      this.billingForm.markAllAsTouched();
    }
  }

  onFormCancel() {
    this.onCancel.emit();
  }

  get isFormValid(): boolean {
    return this.billingForm.valid;
  }

  get hasChanges(): boolean {
    return this.billingForm.dirty;
  }
}
